import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Drawer, Grid, Space, Typography, Tooltip, Select, theme, Badge } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  MenuOutlined,
  LockOutlined,
  LogoutOutlined,
  DownOutlined,
  SunOutlined,
  MoonOutlined,
  PlayCircleOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { versionService } from '../../services/versionService';
import ChangePasswordModal from '../common/ChangePasswordModal';
import AdminTour from '../onboarding/AdminTour';
import WhatsNewModal from '../whatsNew/WhatsNewModal';
import { getLatestReleaseVersion, isNewerVersion } from '../../utils/version';
import packageJson from '../../../package.json';

const UNKNOWN_VERSION_PLACEHOLDER = '—';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;
const { useToken } = theme;
const hasAnyRole = (user, roles) => user?.roles?.some((role) => roles.includes(role));

const MainLayout = ({ children }) => {
  const { user, logout, canAccessEmployees, canViewDashboard, canViewDepartments, isAdmin, isHR } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { token } = useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const hasTriggeredRef = useRef(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!user || hasTriggeredRef.current || !screens.md) return;
    const isAdminOrHR = isAdmin() || isHR();
    if (!isAdminOrHR) return;
    hasTriggeredRef.current = true;
    const latestVersion = getLatestReleaseVersion();
    if (user.tutorialStatus === 'NOT_STARTED') {
      setTourOpen(true);
    } else if (latestVersion && isNewerVersion(latestVersion, user.whatsNewSeenVersion)) {
      setWhatsNewOpen(true);
    }
  }, [user, screens.md]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    versionService
      .getBackendVersion()
      .then((data) => {
        if (!cancelled) {
          setBackendVersion(data?.version ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBackendVersion(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canShowDashboard = canViewDashboard?.() || hasAnyRole(user, ['ROLE_ADMIN', 'ROLE_HR', 'ROLE_RECEPTION', 'ADMIN', 'HR', 'RECEPTION']);
  const canShowDepartments = canViewDepartments?.() || hasAnyRole(user, ['ROLE_ADMIN', 'ROLE_HR', 'ROLE_RECEPTION', 'ADMIN', 'HR', 'RECEPTION']);
  const canShowEmployees = canAccessEmployees?.() || hasAnyRole(user, ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR']);

  const menuConfig = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('nav.dashboard'),
      isVisible: ({ canShowDashboardMenu }) => canShowDashboardMenu(),
      testId: 'nav-dashboard',
    },
    {
      key: '/departments',
      icon: <TeamOutlined />,
      label: t('nav.departments'),
      isVisible: ({ canShowDepartmentsMenu }) => canShowDepartmentsMenu(),
      testId: 'nav-departments',
    },
    {
      key: '/employees',
      icon: <UserOutlined />,
      label: t('nav.employees'),
      isVisible: ({ canAccessEmployees: canAccess }) => canAccess(),
      testId: 'nav-employees',
    },
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: t('nav.calendar'),
      isVisible: () => true,
      testId: 'nav-calendar',
    },
    {
      key: '/leave',
      icon: <FileTextOutlined />,
      label: t('nav.leaveRequests'),
      isVisible: () => true,
      testId: 'nav-leave',
    },
  ];

  const menuItems = menuConfig
    .filter((item) => item.isVisible({
      canShowDashboardMenu: () => canShowDashboard,
      canShowDepartmentsMenu: () => canShowDepartments,
      canAccessEmployees: () => canShowEmployees,
    }))
    .map((item) => ({
      key: item.key,
      icon: item.icon,
      label: <span data-testid={item.testId}>{item.label}</span>,
    }));

  const handleMenuClick = ({ key }) => {
    navigate(key);
    setMobileDrawerVisible(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'frontend-version',
      label: (
        <span data-testid="frontend-version">
          {t('nav.frontendVersion', { version: packageJson.version })}
        </span>
      ),
      disabled: true,
    },
    {
      key: 'backend-version',
      label: (
        <span data-testid="backend-version">
          {t('nav.backendVersion', { version: backendVersion ?? UNKNOWN_VERSION_PLACEHOLDER })}
        </span>
      ),
      disabled: true,
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: <span data-testid="change-password">{t('changePassword.menuItem')}</span>,
      onClick: () => setChangePasswordOpen(true),
    },
    ...(isAdmin() || isHR() ? [
      {
        key: 'tutorial',
        icon: <PlayCircleOutlined />,
        label: <span data-testid="tutorial-menu-item">{t('tutorial.menuItem')}</span>,
        onClick: () => setTourOpen(true),
      },
      {
        key: 'whats-new',
        icon: <BulbOutlined />,
        label: (
          <Badge dot={!!getLatestReleaseVersion() && isNewerVersion(getLatestReleaseVersion(), user?.whatsNewSeenVersion)} offset={[6, 0]}>
            <span data-testid="whats-new-menu-item">{t('whatsNew.menuItem')}</span>
          </Badge>
        ),
        onClick: () => setWhatsNewOpen(true),
      },
    ] : []),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  const userMenu = {
    items: userMenuItems,
  };

  const siderStyle = {
    overflow: 'auto',
    height: '100vh',
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    background: isDarkMode ? '#020617' : '#0f172a',
    transition: 'background-color 0.3s ease',
  };

  const headerStyle = {
    padding: '0 24px',
    background: token.colorBgContainer,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    transition: 'background-color 0.3s ease',
  };

  const logoContainerStyle = {
    height: 32,
    margin: 16,
    background: 'rgba(13, 148, 136, 0.2)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
  };

  const renderMenu = () => (
    <Menu
      theme="dark"
      mode={screens.md ? "inline" : "vertical"}
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={handleMenuClick}
      style={{ background: 'transparent', borderRight: 'none' }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {screens.md ? (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={siderStyle}
        >
          <div style={logoContainerStyle}>
            {!collapsed && (
              <Text style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>
                {t('layout.logoShort')}
              </Text>
            )}
          </div>
          {renderMenu()}
        </Sider>
      ) : null}

      <Layout style={{
        marginLeft: screens.md ? (collapsed ? 80 : 200) : 0,
        transition: 'margin-left 0.2s ease',
      }}>
        <Header style={headerStyle}>
          <Space>
            {!screens.md && (
              <Button
                type="text"
                className="touch-button"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerVisible(true)}
                style={{ fontSize: 20 }}
              />
            )}
          </Space>

          <Space size="middle">
            <Select
              value={i18n.language}
              onChange={(lang) => i18n.changeLanguage(lang)}
              size="small"
              options={[
                { value: 'ro', label: 'RO' },
                { value: 'en', label: 'EN' },
              ]}
              style={{ width: 65 }}
              data-testid="language-switcher"
            />
            <Tooltip title={isDarkMode ? t('layout.lightMode') : t('layout.darkMode')}>
              <Button
                type="text"
                className="touch-button"
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                style={{
                  fontSize: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label={isDarkMode ? t('layout.lightMode') : t('layout.darkMode')}
                data-testid="theme-toggle"
              />
            </Tooltip>
            {screens.md && (
              <Text style={{ color: token.colorTextSecondary }}>{t('layout.welcome', { username: user?.username })}</Text>
            )}
            <Dropdown menu={userMenu} trigger={['click']}>
              <Button
                type="text"
                className="touch-button"
                style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                data-testid="user-menu"
                aria-label={t('layout.userMenuAria')}
              >
                <Space>
                  <Avatar
                    icon={<UserOutlined />}
                    style={{ backgroundColor: token.colorPrimary }}
                  />
                  <DownOutlined style={{ fontSize: 12 }} />
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: screens.md ? '24px' : '12px 8px',
            minHeight: 280,
          }}
        >
          {children}
        </Content>
      </Layout>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <AdminTour
        open={tourOpen}
        onClose={(finished) => {
          setTourOpen(false);
          if (finished && getLatestReleaseVersion() && isNewerVersion(getLatestReleaseVersion(), user?.whatsNewSeenVersion)) {
            setWhatsNewOpen(true);
          }
        }}
      />

      <WhatsNewModal
        open={whatsNewOpen}
        onClose={() => setWhatsNewOpen(false)}
      />

      {/* Mobile Drawer */}
      <Drawer
        title={t('layout.mobileMenu')}
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        bodyStyle={{ padding: 0, background: isDarkMode ? '#020617' : '#0f172a' }}
        headerStyle={{ background: isDarkMode ? '#020617' : '#0f172a', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        {renderMenu()}
      </Drawer>
    </Layout>
  );
};

export default MainLayout;
